/**
 * PlantSLABoard — Iter 400.
 *
 * Tenant-wide site uptime table. Rows sorted worst-first so P&L pain
 * is visible at a glance. Click a row to drill into a per-site
 * event timeline (down/up transitions + linked tickets).
 *
 * Filters:
 *   • Window (last 7 / 30 / 90 / 365 days, or custom)
 *   • Optional customer_id filter to scope one client's plants
 *
 * SLA math is done server-side (walks plant_status_events). This
 * component just renders the rollup.
 */
import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { AlertCircle, TrendingDown, TrendingUp, Clock, RefreshCw, ChevronRight, X, Download } from "lucide-react";
import api, { formatErr } from "../../lib/api";

const WINDOW_OPTIONS = [
  { key: "7d",   label: "7 days",   days: 7 },
  { key: "30d",  label: "30 days",  days: 30 },
  { key: "90d",  label: "90 days",  days: 90 },
  { key: "365d", label: "12 months", days: 365 },
];

function isoDaysAgo(days) {
  const d = new Date();
  d.setUTCDate(d.getUTCDate() - days);
  return d.toISOString();
}

function fmtPct(v) {
  if (v === null || v === undefined || Number.isNaN(v)) return "—";
  return `${v.toFixed(2)}%`;
}

function fmtDate(iso) {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleString("en-GB", {
      day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit",
    });
  } catch {
    return iso.slice(0, 16).replace("T", " ");
  }
}

function fmtHours(h) {
  if (h === null || h === undefined) return "—";
  if (h < 1) return `${Math.round(h * 60)}m`;
  return `${h.toFixed(1)}h`;
}

function uptimeColor(pct) {
  if (pct >= 99.5) return "text-emerald-300";
  if (pct >= 95) return "text-amber-300";
  return "text-rose-300";
}

// Iter 400 — hex counterpart of uptimeColor(). SLAStat expects a real
// CSS colour on the `accent` prop (used inline via style.color). Passing
// the Tailwind class name works ONLY where it's used as className; the
// drawer's uptime stat card previously received "emerald"/"amber"/"rose"
// which are not valid CSS colours and silently rendered as default text.
function uptimeAccent(pct) {
  if (pct >= 99.5) return "#10B981";
  if (pct >= 95) return "#F59E0B";
  return "#F43F5E";
}

export default function PlantSLABoard({ customerId }) {
  const [windowKey, setWindowKey] = useState("30d");
  const [rows, setRows] = useState([]);
  const [totals, setTotals] = useState(null);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState("");
  const navigate = useNavigate();

  const days = useMemo(
    () => WINDOW_OPTIONS.find((w) => w.key === windowKey)?.days || 30,
    [windowKey],
  );

  const load = async () => {
    setLoading(true);
    setErr("");
    try {
      const from = isoDaysAgo(days);
      const params = { from };
      if (customerId) params.customer_id = customerId;
      const { data } = await api.get("/plant-sla/sites", { params });
      setRows(data.rows || []);
      setTotals(data.totals || null);
    } catch (e) {
      setErr(formatErr(e));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [days, customerId]);

  return (
    <div className="space-y-3" data-testid="plant-sla-board">
      {/* Toolbar */}
      <div className="flex flex-wrap items-center gap-2">
        <div className="flex items-center gap-1 bg-white/[0.02] border border-white/10 rounded-md p-1">
          {WINDOW_OPTIONS.map((w) => (
            <button
              key={w.key}
              type="button"
              onClick={() => setWindowKey(w.key)}
              className={`px-2.5 py-1 rounded text-[11px] font-mono uppercase tracking-wider transition ${
                windowKey === w.key
                  ? "bg-[#22d3ee]/20 text-[#22d3ee]"
                  : "text-[#a0aabf] hover:text-white"
              }`}
              data-testid={`sla-window-${w.key}`}
            >
              {w.label}
            </button>
          ))}
        </div>
        <button
          type="button"
          onClick={load}
          disabled={loading}
          className="hv-btn hv-btn-ghost text-xs ml-auto"
          data-testid="sla-refresh"
          title="Recompute uptime"
        >
          <RefreshCw className={`w-3.5 h-3.5 ${loading ? "animate-spin" : ""}`} /> Refresh
        </button>
      </div>

      {/* Totals strip */}
      {totals && (
        <div className="grid grid-cols-3 gap-2">
          <SLAStat
            icon={AlertCircle}
            label="Sites tracked"
            value={totals.sites}
            accent="#1FB6C1"
          />
          <SLAStat
            icon={TrendingDown}
            label="Currently down"
            value={totals.currently_down}
            accent={totals.currently_down > 0 ? "#F43F5E" : "#647087"}
          />
          <SLAStat
            icon={TrendingUp}
            label="Avg uptime"
            value={fmtPct(totals.avg_uptime_pct)}
            accent={totals.avg_uptime_pct >= 99 ? "#10B981" : totals.avg_uptime_pct >= 95 ? "#F59E0B" : "#F43F5E"}
          />
        </div>
      )}

      {err && (
        <div className="hv-card border-rose-500/40 bg-rose-500/10 p-3 text-sm text-rose-200" data-testid="sla-error">
          {err}
        </div>
      )}

      {/* Sites table */}
      {loading && !rows.length ? (
        <div className="hv-card p-6 text-center text-sm text-[#647087]">Loading uptime rollup…</div>
      ) : rows.length === 0 ? (
        <div className="hv-card p-6 text-center text-sm text-[#647087]" data-testid="sla-empty">
          No sites with SLA data yet. Log a support ticket with the &quot;Plant is DOWN&quot; flag to start tracking.
        </div>
      ) : (
        <div className="hv-card overflow-hidden" data-testid="sla-table">
          <div className="hidden sm:grid picking-row-grid-sla items-center gap-2 px-3 py-2 text-[9px] uppercase tracking-wider text-[#647087] font-bold bg-white/[0.03] border-b border-white/10"
               style={{ gridTemplateColumns: "12px minmax(0, 1.5fr) minmax(0, 1fr) 80px 70px 70px 24px" }}>
            <span />
            <span>Site</span>
            <span>Customer</span>
            <span className="text-right">Uptime</span>
            <span className="text-right">Incidents</span>
            <span className="text-right">MTTR</span>
            <span />
          </div>
          <div className="divide-y divide-white/5">
            {rows.map((r) => (
              <button
                key={r.site_id}
                type="button"
                onClick={() => navigate(`/performance/plant-uptime/${r.site_id}?preset=${windowKey}`)}
                className="w-full grid gap-2 items-center px-3 py-2 text-left hover:bg-white/[0.04] transition"
                style={{ gridTemplateColumns: "12px minmax(0, 1.5fr) minmax(0, 1fr) 80px 70px 70px 24px" }}
                data-testid={`sla-row-${r.site_id}`}
              >
                <span
                  className={`w-2 h-2 rounded-full justify-self-center ${
                    r.currently_down ? "bg-rose-400 animate-pulse" : "bg-emerald-400"
                  }`}
                  title={r.currently_down ? "Currently DOWN" : "Running"}
                />
                <span className="text-[13px] text-white truncate">{r.site_name}</span>
                <span className="text-[11px] text-[#a0aabf] truncate">{r.customer_name || "—"}</span>
                <span className={`text-right text-[12px] font-mono tabular-nums ${uptimeColor(r.uptime_pct)}`}>
                  {fmtPct(r.uptime_pct)}
                </span>
                <span className="text-right text-[12px] font-mono text-[#cdd5e3]">
                  {r.incidents}
                </span>
                <span className="text-right text-[12px] font-mono text-[#cdd5e3]">
                  {fmtHours(r.mttr_hours)}
                </span>
                <ChevronRight className="w-3.5 h-3.5 text-[#647087]" />
              </button>
            ))}
          </div>
        </div>
      )}

      {/* Iter 418.aj — SiteTimelineDrawer replaced with full-page
          /performance/plant-uptime/:siteId route (date range picker,
          inline ticket resolution notes, larger event timeline). */}
    </div>
  );
}


function SLAStat({ icon: Icon, label, value, accent }) {
  return (
    <div
      className="rounded p-2 border border-white/5 bg-white/[0.02]"
      data-testid={`sla-stat-${(label || "").toLowerCase().replace(/\s+/g, "-")}`}
    >
      <div className="text-[9px] uppercase tracking-wider text-[#647087] flex items-center gap-1">
        {Icon && <Icon className="w-3 h-3" style={{ color: accent }} />}
        {label}
      </div>
      <div className="text-base font-bold leading-tight mt-0.5" style={{ color: accent }}>
        {value}
      </div>
    </div>
  );
}

